// pages/CreateEvent.js
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useAuth } from '../services/auth';
import { FiMoreHorizontal, FiFileText, FiTrash2 } from 'react-icons/fi';
import '../styles/CreateEvent.scss';

// Components
import TabNavigation from '../components/create/TabNavigation';
import CoverImageSection from '../components/create/CoverImageSection';
import BasicSection from '../components/create/BasicSection';
import RSVPSection from '../components/create/RSVPSection';
import PassesSection from '../components/create/PassesSection';
import MoreSection from '../components/create/MoreSection';

// Modals
import CoverImageModal from '../components/create/CoverImageModal';
import LocationSearchModal from '../components/create/LocationSearchModal';

// Services
import { createEvent, updateEvent, uploadEventCoverImage, getEventById } from '../services/eventService';
import { getCommunityById } from '../services/communityService';
import supabase from '../services/supabase';

// Default background images - stored in public../assets/images/ folder
const DEFAULT_BACKGROUNDS = [
  '/images/backgrounds/1.jpg',
  '/images/backgrounds/2.jpg',
  '/images/backgrounds/3.jpg',
  '/images/backgrounds/4.jpg',
  '/images/backgrounds/6.jpg',
  '/images/backgrounds/7.jpg',
  '/images/backgrounds/8.jpg',
];


const getRandomBackground = () => {
  return DEFAULT_BACKGROUNDS[Math.floor(Math.random() * DEFAULT_BACKGROUNDS.length)];
};

export default function CreateEvent() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const { eventId } = useParams();
  
  // Edit mode detection
  const isEditMode = !!eventId || searchParams.get('mode') === 'edit';
  const editEventId = eventId || searchParams.get('eventId');
  const communityId = searchParams.get('communityId');
  
  const scrollRef = useRef(null);
  const defaultBackgroundRef = useRef(getRandomBackground());

  // Tab state
  const [activeTab, setActiveTab] = useState('basic');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // The ⋯ menu in the header: Save as draft, and Delete when editing.
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  // The loaded invitation's status, so saving a draft normally publishes it.
  const [editStatus, setEditStatus] = useState(null);
  // Why this invitation cannot be deleted, or null; undefined while checking.
  const [deleteBlocker, setDeleteBlocker] = useState(undefined);
  const [isDeleting, setIsDeleting] = useState(false);

  // ========== BASIC INFO STATE ==========
  const [title, setTitle] = useState('');
  const [eventType, setEventType] = useState('');
  const [description, setDescription] = useState('');
  const [coverImage, setCoverImage] = useState(null);
  const [blurredBackground, setBlurredBackground] = useState(null);
  const [coverImageChanged, setCoverImageChanged] = useState(false);

  // Date/Time state
  const [startDate, setStartDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [endTime, setEndTime] = useState(null);

  // Location state
  const [location, setLocation] = useState(null);
  const [locationDetails, setLocationDetails] = useState(null);

  // Photo Pool & Comments
  const [allowGuestUploads, setAllowGuestUploads] = useState(true);
  const [allowGuestComments, setAllowGuestComments] = useState(true);

  // Custom fields and polls
  const [customFields, setCustomFields] = useState([]);
  const [polls, setPolls] = useState([]);

  // Playlists
  const [playlist, setPlaylist] = useState('');
  const [youtubePlaylist, setYoutubePlaylist] = useState('');

  // ========== RSVP STATE ==========
  const [privacy, setPrivacy] = useState('public');
  const [rsvpDeadline, setRsvpDeadline] = useState(null);
  const [maxAttendees, setMaxAttendees] = useState(null);
  const [plusOnesAllowed, setPlusOnesAllowed] = useState(0);
  const [hideMaybeRsvp, setHideMaybeRsvp] = useState(false);
  const [enableWaitlist, setEnableWaitlist] = useState(false);
  const [hideLocationBeforeVerification, setHideLocationBeforeVerification] = useState(false);
  const [emojiSet, setEmojiSet] = useState('basic');
  const [customEmojis, setCustomEmojis] = useState({ going: '✅', maybe: '🤔', notGoing: '❌' });
  const [guestListIds, setGuestListIds] = useState([]);
  const [showInExploreFeed, setShowInExploreFeed] = useState(false);

  // ========== PASSES STATE ==========
  const [passes, setPasses] = useState([]);
  const [globalPassType, setGlobalPassType] = useState({ id: 'free', label: 'Free Event' });
  const [isGstRegistered, setIsGstRegistered] = useState(false);
  const [gstNumber, setGstNumber] = useState('');
  const [panNumber, setPanNumber] = useState('');
  const [bankDetails, setBankDetails] = useState({});
  const [contactPerson, setContactPerson] = useState({});
  const [originalBankAccountId, setOriginalBankAccountId] = useState(null);
  const [hasSales, setHasSales] = useState(false);

  // ========== MORE STATE ==========
  const [additionalInfoFields, setAdditionalInfoFields] = useState([]);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [createdEventId, setCreatedEventId] = useState(null);
  const [linkedCommunity, setLinkedCommunity] = useState(null);
  const [currentFinancialYear, setCurrentFinancialYear] = useState(null);

  // Modals
  const [showCoverImageModal, setShowCoverImageModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);

  // Validation errors
  const [errors, setErrors] = useState({});

  // ========== EFFECTS ==========
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // ── Phone verification ──────────────────────────────────────────────────
  //
  // Required to CREATE an invitation, same as the app. Signup is email-first,
  // so a verified phone is asked for here, the one place it protects anyone.
  // Editing an existing invitation is never blocked. Asked ONCE per visit —
  // the ref stops a re-render or a profile refresh raising it again.
  const [phoneVerified, setPhoneVerified] = useState(null);
  const phonePromptShownRef = useRef(false);

  useEffect(() => {
    if (!user || isEditMode) return;
    let cancelled = false;
    supabase
      .from('users')
      .select('phone_verified')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        const verified = data?.phone_verified === true;
        setPhoneVerified(verified);
        if (!verified && !phonePromptShownRef.current) {
          phonePromptShownRef.current = true;
          const go = window.confirm(
            'To create invitations, verify your phone number first. It keeps hosts accountable to their guests. '
            + 'You can still view invitations and cards without it. Verify your phone now?'
          );
          if (go) navigate('/account-details');
        }
      });
    return () => { cancelled = true; };
  }, [user, isEditMode, navigate]);

  // Initialize with default background on mount (only for create mode)
  useEffect(() => {
    if (!isEditMode) {
      const defaultBgPath = defaultBackgroundRef.current;
      setCoverImage({
        uri: defaultBgPath,
        type: 'default',
        isAnimated: false
      });
      // Set blurred background to same image
      setBlurredBackground(defaultBgPath);
    }
  }, [isEditMode]);

  // Load event data if in edit mode
  useEffect(() => {
    if (isEditMode && editEventId) {
      loadEventForEdit();
    }
  }, [isEditMode, editEventId]);

  // Load community if pre-selected
  useEffect(() => {
    if (communityId && !isEditMode) {
      loadCommunity(communityId);
    }
  }, [communityId, isEditMode]);

  // ========== LOAD COMMUNITY ==========
  const loadCommunity = async (id) => {
    try {
      const community = await getCommunityById(id);
      if (community) {
        setLinkedCommunity(community);
        console.log('✅ Community pre-selected:', community.name);
      }
    } catch (error) {
      console.error('Error loading community:', error);
    }
  };

  // ========== LOAD EVENT FOR EDIT ==========
  const loadEventForEdit = async () => {
    try {
      setIsLoading(true);
      const event = await getEventById(editEventId);
      
      if (!event) {
        throw new Error('Event not found');
      }

      // Check if user is the host
      if (event.host_id !== user.id) {
        alert('You do not have permission to edit this event');
        navigate('/dashboard');
        return;
      }

      // Store financial year
      setCurrentFinancialYear(event.financial_year);
      setCreatedEventId(event.id);

      // Populate all fields from event data
      setTitle(event.title || '');
      setEditStatus(event.status ?? null);
      setEventType(event.event_type || '');
      setDescription(event.description || '');
      
      // Cover image - handle both GIF and regular images
      if (event.cover_image_type === 'gif') {
        const gifUri = event.gif_cover_cdn_url || event.gif_cover_direct_url;
        setCoverImage({
          uri: gifUri,
          cdnUrl: event.gif_cover_cdn_url,
          b2Url: event.gif_cover_direct_url,
          isAnimated: true,
          type: 'gif'
        });
        setBlurredBackground(gifUri);
      } else if (event.cover_image_cdn_url || event.cover_image_direct_url) {
        const imageUri = event.cover_image_cdn_url || event.cover_image_direct_url;
        setCoverImage({
          uri: imageUri,
          cdnUrl: event.cover_image_cdn_url,
          b2Url: event.cover_image_direct_url,
          isAnimated: false,
          type: 'image'
        });
        setBlurredBackground(imageUri);
      }

      // Dates and times
      if (event.start_datetime) {
        const startDateTime = new Date(event.start_datetime);
        setStartDate(startDateTime);
        setStartTime(startDateTime);
      }
      if (event.end_datetime) {
        const endDateTime = new Date(event.end_datetime);
        setEndDate(endDateTime);
        setEndTime(endDateTime);
      }

      // Location
      if (event.location_name) {
        setLocation({
          display_name: event.location_name,
          formatted_address: event.location_address,
          lat: event.location_lat,
          lng: event.location_lng,
          place_id: event.location_place_id,
        });
      }

      // Settings
      setAllowGuestUploads(event.allow_guest_uploads ?? true);
      setAllowGuestComments(event.allow_guest_comments ?? true);
      setCustomFields(event.custom_fields || []);
      setPolls(event.polls || []);
      setPlaylist(event.spotify_playlist || '');
      setYoutubePlaylist(event.youtube_playlist || '');

      // RSVP Settings
      setPrivacy(event.privacy || 'public');
      setRsvpDeadline(event.rsvp_deadline ? new Date(event.rsvp_deadline) : null);
      setMaxAttendees(event.max_attendees || null);
      setPlusOnesAllowed(event.plus_ones_allowed || 0);
      setHideMaybeRsvp(event.hide_maybe_rsvp ?? false);
      setEnableWaitlist(event.enable_waitlist ?? false);
      setHideLocationBeforeVerification(event.hide_location_before_verification ?? false);
      setShowInExploreFeed(event.show_in_explore_feed ?? false);
      setEmojiSet(event.emoji_set || 'basic');
      
      if (event.custom_emojis) {
        setCustomEmojis(event.custom_emojis);
      }

      // Guest lists
      if (event.guest_list_ids) {
        setGuestListIds(event.guest_list_ids);
      }

      // Passes
      if (event.passes && event.passes.length > 0) {
        setPasses(event.passes);
        const isPaid = event.passes.some(p => p.price > 0);
        setGlobalPassType(isPaid 
          ? { id: 'paid', label: 'Paid Event' }
          : { id: 'free', label: 'Free Event' }
        );
      }

      // Financial info
      if (event.bank_account_id) {
        setOriginalBankAccountId(event.bank_account_id);
        // Load bank details
        const { data: bankData } = await supabase
          .from('bank_accounts')
          .select('*')
          .eq('id', event.bank_account_id)
          .single();
        
        if (bankData) {
          setBankDetails({
            accountHolderName: bankData.account_holder_name,
            accountNumber: bankData.account_number,
            ifscCode: bankData.ifsc_code,
            bankName: bankData.bank_name,
          });
        }
      }

      setIsGstRegistered(event.is_gst_registered ?? false);
      setGstNumber(event.gst_number || '');
      setPanNumber(event.pan_number || '');
      
      if (event.contact_person) {
        setContactPerson(event.contact_person);
      }

      // Check if there are sales
      const { data: salesData, error: salesError } = await supabase
        .from('ticket_sales')
        .select('id')
        .eq('event_id', editEventId)
        .limit(1);
      
      if (salesData && salesData.length > 0) {
        setHasSales(true);
      }

      // Additional info fields
      if (event.additional_info_fields) {
        setAdditionalInfoFields(event.additional_info_fields);
      }

      // Linked community
      if (event.community_id) {
        const community = await getCommunityById(event.community_id);
        if (community) {
          setLinkedCommunity(community);
        }
      }

    } catch (error) {
      console.error('Error loading event:', error);
      alert('Failed to load event data');
      navigate('/dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  // ========== IMAGE HANDLERS ==========
  const handleImageSelect = async (image) => {
    try {
      setCoverImage(image);
      setCoverImageChanged(true);
      setShowCoverImageModal(false);
      
      // Set blurred background
      setBlurredBackground(image.uri);
      
      console.log('✅ Image selected:', image.type);
    } catch (error) {
      console.error('Error selecting image:', error);
      alert('Failed to process image');
    }
  };

  const handleLocationSelect = (selectedLocation) => {
    setLocation(selectedLocation);
    setShowLocationModal(false);
  };

  // ========== VALIDATION ==========
  const validateForm = () => {
    const newErrors = {};

    // Basic validation
    if (!title.trim()) newErrors.title = 'Event title is required';
    if (!eventType) newErrors.eventType = 'Event type is required';
    if (!startDate) newErrors.startDate = 'Start date is required';
    if (!startTime) newErrors.startTime = 'Start time is required';

    // RSVP validation
    if (!rsvpDeadline) newErrors.rsvpDeadline = 'RSVP deadline is required';

    // Start datetime validation
    if (startDate && startTime) {
      const startDateTime = new Date(startDate);
      startDateTime.setHours(startTime.getHours(), startTime.getMinutes());
      
      if (startDateTime < new Date()) {
        newErrors.startDateTime = 'Event start time must be in the future';
      }

      // RSVP deadline must be before event
      if (rsvpDeadline && rsvpDeadline > startDateTime) {
        newErrors.rsvpDeadline = 'RSVP deadline must be before event starts';
      }
    }

    // Passes validation for paid events
    if (globalPassType.id === 'paid' || globalPassType.id === 'donation') {
      if (passes.length === 0) {
        newErrors.passes = 'At least one pass type is required for paid events';
      }

      // Bank details validation
      if (!bankDetails.accountHolderName) {
        newErrors.bankDetails = 'Bank account holder name is required';
      }
      if (!bankDetails.accountNumber) {
        newErrors.bankDetails = 'Bank account number is required';
      }
      if (!bankDetails.ifscCode) {
        newErrors.bankDetails = 'IFSC code is required';
      }

      // Contact person validation
      if (!contactPerson.name) {
        newErrors.contactPerson = 'Contact person name is required';
      }
      if (!contactPerson.email) {
        newErrors.contactPerson = 'Contact person email is required';
      }
      if (!contactPerson.phone) {
        newErrors.contactPerson = 'Contact person phone is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ========== TAB CHANGE ==========
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  };

  // ========== CREATE/UPDATE EVENT ==========
  const handleCreateEvent = async ({ asDraft = false } = {}) => {
    setShowMoreMenu(false);

    // Checked again at save: the prompt can be dismissed, and the database
    // is not the place to discover this after uploading a cover.
    if (!isEditMode && phoneVerified === false) {
      if (window.confirm('Verify your phone number to create invitations. Go to Account Details?')) {
        navigate('/account-details');
      }
      return;
    }

    // A draft is for an unfinished invitation, so it skips the publish
    // checks. A title is the one thing needed, to find it again.
    if (asDraft) {
      if (!title.trim()) {
        alert('Give your draft a title so you can find it later.');
        setActiveTab('basic');
        return;
      }
    } else if (!validateForm()) {
      alert('Please fix the errors before submitting');
      setActiveTab('basic');
      return;
    }

    try {
      setIsSaving(true);

      // Combine date and time for start
      const combinedStartDateTime = startDate ? new Date(startDate) : null;
      if (combinedStartDateTime && startTime) {
        combinedStartDateTime.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);
      }

      // Combine date and time for end (if provided)
      let combinedEndDateTime = null;
      if (endDate && endTime) {
        combinedEndDateTime = new Date(endDate);
        combinedEndDateTime.setHours(endTime.getHours(), endTime.getMinutes(), 0, 0);
      } else if (endTime) {
        combinedEndDateTime = new Date(startDate);
        combinedEndDateTime.setHours(endTime.getHours(), endTime.getMinutes(), 0, 0);
      }

      // Prepare event data
      const eventData = {
        title: title.trim(),
        event_type: eventType,
        description: description.trim(),
        start_datetime: combinedStartDateTime ? combinedStartDateTime.toISOString() : null,
        end_datetime: combinedEndDateTime ? combinedEndDateTime.toISOString() : null,
        location_name: location?.display_name || null,
        location_address: location?.formatted_address || null,
        location_lat: location?.lat || null,
        location_lng: location?.lng || null,
        location_place_id: location?.place_id || null,
        allow_guest_uploads: allowGuestUploads,
        allow_guest_comments: allowGuestComments,
        custom_fields: customFields,
        polls: polls,
        spotify_playlist: playlist || null,
        youtube_playlist: youtubePlaylist || null,
        privacy: privacy,
        rsvp_deadline: rsvpDeadline ? rsvpDeadline.toISOString() : null,
        max_attendees: maxAttendees,
        plus_ones_allowed: plusOnesAllowed,
        hide_maybe_rsvp: hideMaybeRsvp,
        enable_waitlist: enableWaitlist,
        hide_location_before_verification: hideLocationBeforeVerification,
        show_in_explore_feed: showInExploreFeed,
        emoji_set: emojiSet,
        custom_emojis: emojiSet === 'custom' ? customEmojis : null,
        guest_list_ids: guestListIds.length > 0 ? guestListIds : null,
        passes: passes.length > 0 ? passes : null,
        is_gst_registered: isGstRegistered,
        gst_number: gstNumber || null,
        pan_number: panNumber || null,
        contact_person: Object.keys(contactPerson).length > 0 ? contactPerson : null,
        additional_info_fields: additionalInfoFields.length > 0 ? additionalInfoFields : null,
        community_id: linkedCommunity?.id || null,
      };

      // Status is set explicitly. Left out, the column's default is 'draft',
      // so every invitation created here used to be saved as one.
      if (asDraft) {
        eventData.status = 'draft';
      } else if (!isEditMode || editStatus === 'draft') {
        eventData.status = 'published';
        eventData.published_at = new Date().toISOString();
      }

      let eventId;

      if (isEditMode) {
        // Update existing event
        await updateEvent(editEventId, eventData);
        eventId = editEventId;
        console.log('✅ Event updated:', eventId);
      } else {
        // Create new event
        const newEvent = await createEvent(eventData);
        eventId = newEvent.id;
        setCreatedEventId(eventId);
        console.log('✅ Event created:', eventId);
      }

      // Upload cover image if changed or new event
      if (coverImageChanged || !isEditMode) {
        if (coverImage && coverImage.type !== 'default') {
          console.log('📸 Uploading cover image...');
          await uploadEventCoverImage(eventId, coverImage);
        }
      }

      // Handle bank details for paid events
      if ((globalPassType.id === 'paid' || globalPassType.id === 'donation') && 
          Object.keys(bankDetails).length > 0) {
        
        // Only create/update bank account if it's new or has changes
        if (!originalBankAccountId || !hasSales) {
          console.log('💰 Processing bank details...');
          
          // Insert or update bank account
          const { data: bankData, error: bankError } = await supabase
            .from('bank_accounts')
            .upsert({
              id: originalBankAccountId || undefined,
              user_id: user.id,
              account_holder_name: bankDetails.accountHolderName,
              account_number: bankDetails.accountNumber,
              ifsc_code: bankDetails.ifscCode,
              bank_name: bankDetails.bankName,
            })
            .select()
            .single();

          if (bankError) throw bankError;

          // Link bank account to event
          await supabase
            .from('events')
            .update({ bank_account_id: bankData.id })
            .eq('id', eventId);
        }
      }

      if (asDraft) {
        alert('Saved as draft. Only you can see it until you publish.');
        navigate('/invitations');
        return;
      }
      alert(isEditMode ? 'Event updated successfully!' : 'Event created successfully!');
      navigate(`/event/${eventId}`);

    } catch (error) {
      console.error('Error creating/updating event:', error);
      alert(error.message || 'Failed to save event. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // ========== DELETE ==========
  // Asked when the menu opens, so the option shows enabled, or disabled with
  // the reason. Deletion is allowed only for the host, and only while no one
  // else has RSVPd or bought a ticket; the database re-checks on delete.
  useEffect(() => {
    if (!showMoreMenu || !isEditMode || !editEventId) return undefined;
    let cancelled = false;
    setDeleteBlocker(undefined);
    supabase.rpc('invitation_delete_blocker', { p_event_id: editEventId })
      .then(({ data, error }) => {
        if (cancelled) return;
        setDeleteBlocker(error ? 'Could not check right now. Try again later.' : (data ?? null));
      });
    return () => { cancelled = true; };
  }, [showMoreMenu, isEditMode, editEventId]);

  const handleDeleteEvent = async () => {
    if (!window.confirm(`Delete "${title || 'this invitation'}" for good, with its link? This cannot be undone.`)) return;
    setIsDeleting(true);
    const { error } = await supabase.rpc('delete_invitation', { p_event_id: editEventId });
    setIsDeleting(false);
    if (error) {
      alert(error.message || 'Could not delete this invitation.');
      return;
    }
    setShowMoreMenu(false);
    navigate('/invitations');
  };

  const alreadyPublished = isEditMode && editStatus !== 'draft';

  // ========== RENDER ==========
  if (isLoading) {
    return (
      <div className="create-event-page">
        <div className="create-event-loading">
          <div className="spinner"></div>
          <p>Loading event data...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="create-event-page"
      style={{
        backgroundImage: blurredBackground ? `url(${blurredBackground})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
    >
      {/* Blur overlay */}
      <div className="blur-overlay"></div>

      {/* Header */}
      <div className="create-event-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <i className="icon-arrow-back"></i>
        </button>
        <h1>{isEditMode ? 'Edit Event' : 'Create Event'}</h1>
        <div className="create-more">
          <button
            type="button"
            className="back-button"
            onClick={() => setShowMoreMenu((v) => !v)}
            aria-label="More options"
            aria-expanded={showMoreMenu}
          >
            <FiMoreHorizontal size={20} />
          </button>
          {showMoreMenu && (
            <>
              <div className="create-more-backdrop" onClick={() => setShowMoreMenu(false)} />
              <div className="create-more-menu" role="menu">
                {/* Disabled once published: turning a live invitation back
                    into a draft would take it offline for every guest. */}
                <button
                  type="button"
                  role="menuitem"
                  className="create-more-item"
                  onClick={() => handleCreateEvent({ asDraft: true })}
                  disabled={isSaving || alreadyPublished}
                >
                  <FiFileText size={16} />
                  <span>Save as draft</span>
                  {alreadyPublished && <small>Already published</small>}
                </button>
                {isEditMode && (
                  <button
                    type="button"
                    role="menuitem"
                    className="create-more-item is-danger"
                    onClick={handleDeleteEvent}
                    disabled={deleteBlocker !== null || isDeleting}
                  >
                    <FiTrash2 size={16} />
                    <span>{isDeleting ? 'Deleting…' : 'Delete invitation'}</span>
                    {deleteBlocker === undefined && <small>Checking…</small>}
                    {deleteBlocker && <small>{deleteBlocker}</small>}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <TabNavigation 
        activeTab={activeTab} 
        onTabChange={handleTabChange}
        tabs={[
          { id: 'basic', label: 'Basic', icon: 'create' },
          { id: 'rsvp', label: 'RSVP', icon: 'people' },
          { id: 'passes', label: 'Passes', icon: 'ticket' },
          { id: 'more', label: 'More', icon: 'ellipsis-horizontal' },
        ]}
      />

      {/* Content */}
      <div className="create-event-content" ref={scrollRef}>
        <div className="content-container">
          {activeTab === 'basic' && (
            <>
              <CoverImageSection
                coverImage={coverImage}
                onPress={() => setShowCoverImageModal(true)}
              />
              
              <BasicSection
                title={title}
                setTitle={setTitle}
                eventType={eventType}
                setEventType={setEventType}
                description={description}
                setDescription={setDescription}
                startDate={startDate}
                setStartDate={setStartDate}
                startTime={startTime}
                setStartTime={setStartTime}
                endDate={endDate}
                setEndDate={setEndDate}
                endTime={endTime}
                setEndTime={setEndTime}
                location={location}
                onLocationPress={() => setShowLocationModal(true)}
                allowGuestUploads={allowGuestUploads}
                setAllowGuestUploads={setAllowGuestUploads}
                allowGuestComments={allowGuestComments}
                setAllowGuestComments={setAllowGuestComments}
                customFields={customFields}
                setCustomFields={setCustomFields}
                polls={polls}
                setPolls={setPolls}
                playlist={playlist}
                setPlaylist={setPlaylist}
                youtubePlaylist={youtubePlaylist}
                setYoutubePlaylist={setYoutubePlaylist}
                errors={errors}
              />
            </>
          )}

          {activeTab === 'rsvp' && (
            <RSVPSection
              privacy={privacy}
              setPrivacy={setPrivacy}
              rsvpDeadline={rsvpDeadline}
              setRsvpDeadline={setRsvpDeadline}
              maxAttendees={maxAttendees}
              setMaxAttendees={setMaxAttendees}
              plusOnesAllowed={plusOnesAllowed}
              setPlusOnesAllowed={setPlusOnesAllowed}
              hideMaybeRsvp={hideMaybeRsvp}
              setHideMaybeRsvp={setHideMaybeRsvp}
              enableWaitlist={enableWaitlist}
              setEnableWaitlist={setEnableWaitlist}
              hideLocationBeforeVerification={hideLocationBeforeVerification}
              setHideLocationBeforeVerification={setHideLocationBeforeVerification}
              emojiSet={emojiSet}
              setEmojiSet={setEmojiSet}
              customEmojis={customEmojis}
              setCustomEmojis={setCustomEmojis}
              guestListIds={guestListIds}
              setGuestListIds={setGuestListIds}
              showInExploreFeed={showInExploreFeed}
              setShowInExploreFeed={setShowInExploreFeed}
              errors={errors}
            />
          )}

          {activeTab === 'passes' && (
            <PassesSection
              passes={passes}
              setPasses={setPasses}
              globalPassType={globalPassType}
              setGlobalPassType={setGlobalPassType}
              isGstRegistered={isGstRegistered}
              setIsGstRegistered={setIsGstRegistered}
              gstNumber={gstNumber}
              setGstNumber={setGstNumber}
              panNumber={panNumber}
              setPanNumber={setPanNumber}
              bankDetails={bankDetails}
              setBankDetails={setBankDetails}
              contactPerson={contactPerson}
              setContactPerson={setContactPerson}
              isEditMode={isEditMode}
              hasSales={hasSales}
              originalBankAccountId={originalBankAccountId}
            />
          )}

          {activeTab === 'more' && (
            <MoreSection
              additionalInfoFields={additionalInfoFields}
              setAdditionalInfoFields={setAdditionalInfoFields}
              eventId={createdEventId}
              financialYear={currentFinancialYear}
              linkedCommunity={linkedCommunity}
              setLinkedCommunity={setLinkedCommunity}
              agreedToTerms={agreedToTerms}
              setAgreedToTerms={setAgreedToTerms}
            />
          )}
        </div>
      </div>

      {/* Bottom Action Bar */}
      <div className="bottom-action-bar">
        <div className="action-bar-content">
          {activeTab === 'basic' && (
            <button
              className="btn btn-primary btn-next"
              onClick={() => handleTabChange('rsvp')}
            >
              <span>Next: RSVP</span>
              <i className="icon-arrow-forward"></i>
            </button>
          )}

          {activeTab === 'rsvp' && (
            <div className="button-group">
              <button
                className="btn btn-secondary"
                onClick={() => handleTabChange('basic')}
              >
                <i className="icon-arrow-back"></i>
                <span>Back</span>
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleTabChange('passes')}
              >
                <span>Next: Passes</span>
                <i className="icon-arrow-forward"></i>
              </button>
            </div>
          )}

          {activeTab === 'passes' && (
            <div className="button-group">
              <button
                className="btn btn-secondary"
                onClick={() => handleTabChange('rsvp')}
              >
                <i className="icon-arrow-back"></i>
                <span>Back</span>
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleTabChange('more')}
              >
                <span>Next: More</span>
                <i className="icon-arrow-forward"></i>
              </button>
            </div>
          )}

          {activeTab === 'more' && (
            <div className="button-group">
              <button
                className="btn btn-secondary"
                onClick={() => handleTabChange('passes')}
              >
                <i className="icon-arrow-back"></i>
                <span>Back</span>
              </button>
              <button
                className={`btn btn-success ${!agreedToTerms || isSaving ? 'disabled' : ''}`}
                onClick={handleCreateEvent}
                disabled={!agreedToTerms || isSaving}
              >
                {isSaving ? (
                  <>
                    <div className="spinner-small"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <i className="icon-checkmark-circle"></i>
                    <span>{isEditMode ? 'Update Event' : 'Create Event'}</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showCoverImageModal && (
        <CoverImageModal
          onClose={() => setShowCoverImageModal(false)}
          onSelectImage={handleImageSelect}
          eventType={eventType}
        />
      )}

      {showLocationModal && (
        <LocationSearchModal
          onClose={() => setShowLocationModal(false)}
          onSelectLocation={handleLocationSelect}
        />
      )}
    </div>
  );
}